import { type ShortUrlResponse } from '@air/api/types';
import { type SegmentedControlOption } from '@air/component-segmented-control';

/**
 * The three states surfaced by the "Allow downloading" share-link setting.
 *
 * - `none`: viewers cannot download anything from the share link.
 * - `single-file`: viewers can download individual or selected assets, but the
 *   bulk "Download board" / "Save all" action is hidden.
 * - `all-assets`: full download access — individual downloads AND the bulk
 *   "Download board" / "Save all" action are visible.
 *
 * Persisted as the pair `(permissions.asset.canViewOriginal, presentationSettings.hideDownloadAll)`.
 */
export type DownloadPermissionMode = 'none' | 'single-file' | 'all-assets';

export const DOWNLOAD_PERMISSION_OPTION_NONE: SegmentedControlOption = {
  label: 'None',
  value: 'none',
  id: 'none',
  tooltip: 'No downloads',
};

export const DOWNLOAD_PERMISSION_OPTION_SINGLE_FILE: SegmentedControlOption = {
  label: 'Single-file only',
  value: 'single-file',
  id: 'single-file',
  tooltip: 'Single-file downloads only',
};

export const DOWNLOAD_PERMISSION_OPTION_ALL_ASSETS: SegmentedControlOption = {
  label: 'All assets',
  value: 'all-assets',
  id: 'all-assets',
  tooltip: 'All downloads, including the bulk "Save all" action',
};

/**
 * Reverse mapping — derive the segmented value from the persisted permission pair.
 *
 * - `canViewOriginal === false` → `none` (downloads disabled entirely; `hideDownloadAll` is ignored)
 * - `canViewOriginal === true` && `hideDownloadAll === true` → `single-file`
 * - `canViewOriginal === true` && `hideDownloadAll !== true` → `all-assets`
 */
export const getDownloadPermissionMode = (
  canViewOriginal: boolean,
  hideDownloadAll: boolean | null | undefined,
): DownloadPermissionMode => {
  if (!canViewOriginal) {
    return 'none';
  }

  return hideDownloadAll ? 'single-file' : 'all-assets';
};

type DownloadPermissionShortUrlUpdate = {
  presentationSettings: Pick<NonNullable<ShortUrlResponse['presentationSettings']>, 'hideDownloadAll'>;
  permissions: {
    asset: Pick<ShortUrlResponse['permissions']['asset'], 'canViewOriginal'>;
  };
};

/**
 * Build the partial short-url payload that mirrors the chosen download mode.
 *
 * Returns a true partial: only `hideDownloadAll` under `presentationSettings`, and only
 * `canViewOriginal` under `permissions.asset`. The backend patch-merges `presentationSettings`
 * field-by-field, so omitting other fields preserves them server-side. Callers that hold
 * the share-link in local React state (e.g. the create-flow staging object) should spread
 * this partial over their existing state so other settings retain their values across
 * separate toggle updates.
 */
export const buildDownloadPermissionShortUrlUpdate = (
  mode: DownloadPermissionMode,
): DownloadPermissionShortUrlUpdate => ({
  presentationSettings: {
    hideDownloadAll: mode !== 'all-assets',
  },
  permissions: {
    asset: {
      canViewOriginal: mode !== 'none',
    },
  },
});
